import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Brain, Building2, Calendar, CheckCircle2, ChevronRight, Clock, Sparkles, Search, Award
} from "lucide-react";

/**
 * "Comment ça marche ?" — large 3-step visual demo section.
 * Receives navigation handlers as props to stay decoupled from page state.
 */
export default function HowItWorksSection({ onTryFree }) {
  return (
    <>
            <section id="demo" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-100 text-violet-700 text-sm mb-6">
              <Clock className="w-4 h-4" />
              <span>Gagnez 10h par semaine</span>
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-semibold text-slate-900 mb-4">
              Comment ça marche ?
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              En 3 étapes simples, trouvez les marchés publics qui vous correspondent
            </p>
          </motion.div>
          
          {/* Step 1 - Profile Selection Mockup */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-12 items-center mb-20"
          >
            <div className="order-2 md:order-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-100 text-violet-700 text-sm font-medium mb-4">
                <span className="w-6 h-6 rounded-full bg-violet-600 text-white flex items-center justify-center text-xs font-bold">1</span>
                Étape 1
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-semibold text-slate-900 mb-4">
                Indiquez votre profil entreprise
              </h3>
              <p className="text-slate-600 text-lg mb-6">
                Sélectionnez votre secteur d'activité, votre région et ajoutez vos mots-clés métier. <strong>Sans inscription</strong>, en 30 secondes.
              </p>
              <ul className="space-y-3">
                {["10 secteurs d'activité couverts", "13 régions françaises", "Mots-clés personnalisés illimités"].map((item, i) => (
                  <li key={`item-${i}`} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="order-1 md:order-2">
              {/* Real UI Mockup - Profile Form */}
              <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                      <Brain className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Essai gratuit</h4>
                      <p className="text-sm text-slate-500">Sans inscription, découvrez vos 3 marchés</p>
                    </div>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Secteur d'activité *</label>
                      <div className="flex items-center justify-between px-3 py-2 border border-slate-300 rounded-lg bg-white">
                        <span className="text-slate-900">Informatique</span>
                        <ChevronRight className="w-4 h-4 text-slate-400 rotate-90" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Région *</label>
                      <div className="flex items-center justify-between px-3 py-2 border border-slate-300 rounded-lg bg-white">
                        <span className="text-slate-900">Île-de-France</span>
                        <ChevronRight className="w-4 h-4 text-slate-400 rotate-90" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Mots-clés</label>
                    <div className="flex gap-2 flex-wrap">
                      <Badge className="bg-violet-100 text-violet-700">cybersécurité</Badge>
                      <Badge className="bg-violet-100 text-violet-700">cloud</Badge>
                      <Badge className="bg-violet-100 text-violet-700">logiciel</Badge>
                    </div>
                  </div>
                  <Button className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-lg py-3">
                    <Brain className="w-4 h-4 mr-2" />
                    Obtenir mes 3 recommandations
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Step 2 - AI Analysis Mockup */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-12 items-center mb-20"
          >
            <div>
              {/* Real UI Mockup - Synthèse */}
              <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
                <div className="p-4 bg-gradient-to-r from-violet-50 to-indigo-50 border-b border-slate-100">
                  <div className="flex items-center gap-2 text-sm text-violet-700">
                    <Sparkles className="w-4 h-4" />
                    <span>Synthèse rapide · Pièces de marché</span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex gap-2 mb-4">
                    <Badge variant="outline" className="border-violet-300 text-violet-700">RC</Badge>
                    <Badge variant="outline" className="border-orange-300 text-orange-700">CCAP</Badge>
                    <Badge variant="outline" className="border-green-300 text-green-700">CCTP</Badge>
                  </div>
                  <div className="space-y-3">
                    <div className="p-3 bg-violet-50 rounded-lg border border-violet-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-violet-600">RC · Règlement de Consultation</span>
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> 28 s
                        </span>
                      </div>
                      <p className="text-sm font-medium text-slate-900">Critères & conditions de participation</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        <Badge variant="secondary" className="text-xs">Critères pondérés</Badge>
                        <Badge variant="secondary" className="text-xs">Date limite</Badge>
                        <Badge variant="secondary" className="text-xs">Variantes</Badge>
                      </div>
                    </div>
                    <div className="p-3 bg-orange-50 rounded-lg border border-orange-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-orange-600">CCAP · Clauses Administratives</span>
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> 31 s
                        </span>
                      </div>
                      <p className="text-sm font-medium text-slate-900">Prix, délais, pénalités & garanties</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-green-600">CCTP · Clauses Techniques</span>
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> 26 s
                        </span>
                      </div>
                      <p className="text-sm font-medium text-slate-900">Spécifications, normes & livrables</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-sm font-medium mb-4">
                <span className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">2</span>
                Étape 2
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-semibold text-slate-900 mb-4">
                L'IA analyse les documents en 30 secondes
              </h3>
              <p className="text-slate-600 text-lg mb-6">
                Notre IA GPT-5.2 lit et extrait automatiquement les informations clés de vos DCE : critères d'attribution, délais, pénalités, exigences techniques. <strong>Fini la lecture de 200 pages.</strong>
              </p>
              <ul className="space-y-3">
                {["RC, CCAP, CCTP analysés automatiquement", "Extraction des critères de notation", "Alertes sur les points de vigilance"].map((item, i) => (
                  <li key={`item-${i}`} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Step 3 - Recommendations Mockup */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-12 items-center mb-16"
          >
            <div className="order-2 md:order-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-medium mb-4">
                <span className="w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold">3</span>
                Étape 3
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-semibold text-slate-900 mb-4">
                Recevez vos recommandations personnalisées
              </h3>
              <p className="text-slate-600 text-lg mb-6">
                Chaque marché recommandé inclut un <strong>score de compatibilité</strong> et les raisons du match. Priorisez vos efforts sur les meilleures opportunités.
              </p>
              <ul className="space-y-3">
                {["Score de compatibilité de 0 à 100%", "Raisons détaillées du matching", "Lien direct vers le DCE complet"].map((item, i) => (
                  <li key={`item-${i}`} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="order-1 md:order-2">
              {/* Real UI Mockup - Recommendations */}
              <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
                <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-slate-100">
                  <div className="flex items-center gap-2 text-sm text-green-700">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>3 marchés recommandés pour votre profil</span>
                  </div>
                </div>
                <div className="p-4 space-y-3">
                  {/* Recommendation Card 1 */}
                  <div className="p-4 border border-l-4 border-l-green-500 border-slate-200 rounded-lg">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-green-100 text-green-700 font-semibold">
                            <Sparkles className="w-3 h-3 mr-1" />
                            92%
                          </Badge>
                          <Badge variant="outline" className="text-xs">BOAMP</Badge>
                        </div>
                        <h4 className="font-medium text-slate-900 text-sm mb-1 line-clamp-2">
                          Maintenance informatique des postes de travail
                        </h4>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            Région Île-de-France
                          </span>
                          <span>·</span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            15/04/2026
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Recommendation Card 2 */}
                  <div className="p-4 border border-l-4 border-l-yellow-500 border-slate-200 rounded-lg">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-yellow-100 text-yellow-700 font-semibold">
                            <Sparkles className="w-3 h-3 mr-1" />
                            78%
                          </Badge>
                          <Badge variant="outline" className="text-xs">TED</Badge>
                        </div>
                        <h4 className="font-medium text-slate-900 text-sm mb-1 line-clamp-2">
                          Fourniture de licences logicielles
                        </h4>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            Ministère de l'Intérieur
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Recommendation Card 3 */}
                  <div className="p-4 border border-l-4 border-l-violet-500 border-slate-200 rounded-lg">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-violet-100 text-violet-700 font-semibold">
                            <Sparkles className="w-3 h-3 mr-1" />
                            71%
                          </Badge>
                          <Badge variant="outline" className="text-xs">DECP</Badge>
                        </div>
                        <h4 className="font-medium text-slate-900 text-sm mb-1 line-clamp-2">
                          Développement application web métier
                        </h4>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            CHU Paris
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Time Savings Banner */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl p-8 md:p-12 text-white text-center"
          >
            <h3 className="font-display text-2xl md:text-3xl font-semibold mb-4">
              Combien de temps économisez-vous ?
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8">
              {[
                { value: "10h", label: "économisées par semaine", icon: Clock },
                { value: "30s", label: "pour analyser un DCE", icon: Sparkles },
                { value: "100%", label: "des sources couvertes", icon: Search },
                { value: "0", label: "marché pertinent manqué", icon: Award }
              ].map((stat, i) => (
                <div key={`item-${i}`} className="text-center">
                  <stat.icon className="w-8 h-8 mx-auto mb-2 text-violet-200" />
                  <div className="text-3xl md:text-4xl font-display font-bold">{stat.value}</div>
                  <div className="text-sm text-violet-200">{stat.label}</div>
                </div>
              ))}
            </div>
            <Button 
              size="lg"
              onClick={() => onTryFree()}
              className="mt-8 bg-white text-violet-700 hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-medium shadow-lg"
              data-testid="demo-cta-btn"
            >
              <Sparkles className="mr-2 w-5 h-5" />
              Essayer maintenant gratuitement
            </Button>
          </motion.div>
        </div>
      </section>
    </>
  );
}
